import type { ReactElement, ReactNode } from 'react';
import { cn } from '@tailoring-crm/ui';

/**
 * One tile of the Home analytics grid: a plain card with a small title and, optionally, the
 * chart/table switch beside it.
 *
 * The panels are deliberately quieter than the coloured tiles above them. The tiles are the
 * morning's questions and earn the colour; the panels are what somebody reads afterwards, and a
 * grid of eight shouting cards is a grid in which nothing is louder than anything else.
 *
 * Each panel is a named region, so a screen reader can jump between "Monthly sales" and "Daily
 * sales" the way a sighted owner jumps between the cards.
 */
export function AnalyticsPanel({
  title,
  actions,
  className,
  children,
}: {
  title: string;
  actions?: ReactNode;
  className?: string | undefined;
  children: ReactNode;
}): ReactElement {
  return (
    <section
      aria-label={title}
      className={cn(
        'flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}
